import type { ConfluencePopularLabelStyle } from '../hooks/__generated__/usePopularLabelsDataQuery.graphql.ts';
import type { StyleType } from '../popularLabelsTypes.ts';

/**
 * Default value for the `count` macro param when it is missing, empty, or
 * doesn't parse to a positive integer.
 */
export const DEFAULT_COUNT = 100;

/**
 * Parses the `count` macro param value into a positive integer, falling back
 * to {@link DEFAULT_COUNT} when the value is missing, non-numeric, or `<= 0`.
 */
export const parseCount = (value: string | undefined): number => {
	if (!value) {
		return DEFAULT_COUNT;
	}
	const parsed = parseInt(value, 10);
	return isNaN(parsed) || parsed <= 0 ? DEFAULT_COUNT : parsed;
};

/**
 * Parses the `style` macro param value into the UI-facing {@link StyleType}
 * used to pick the layout component. Any unrecognized value falls back to
 * `'list'`, matching the legacy macro behaviour.
 */
export const parseStyle = (value: string | undefined): StyleType => {
	return value === 'heatmap' ? 'heatmap' : 'list';
};

/**
 * Converts the UI-facing {@link StyleType} into the GraphQL enum value
 * accepted by `usePopularLabelsDataQuery`.
 */
export const toStyleEnum = (style: StyleType): ConfluencePopularLabelStyle => {
	return style === 'heatmap' ? 'HEATMAP' : 'LIST';
};

/**
 * Legacy Confluence sentinel value that authors / templates may set as the
 * `spaceKey` macro param to mean "use the space the page lives in". The
 * extension handler passes the current page's space key alongside the
 * params; this helper substitutes it in.
 */
export const CURRENT_SPACE_KEY_TOKEN = 'currentSpace()';

/**
 * Resolves the raw `spaceKey` macro param value to the effective space key
 * that should be used to scope the popular-labels query.
 *
 * Behaviour mirrors the legacy backend macro:
 *   - `undefined` / empty   → `undefined` (caller should fetch global labels)
 *   - `'currentSpace()'`    → `currentSpaceKey` (the space the page lives in).
 *                             When the macro is rendered outside any space
 *                             context (e.g. a personal page) and no current
 *                             space key is available, we preserve the literal
 *                             token so downstream space-key resolution falls
 *                             into its "no such space" branch — matching the
 *                             behaviour of `content-report-table-macro`.
 *   - any other value       → the value as-is.
 */
export const resolveSpaceKey = (
	rawSpaceKey: string | undefined,
	currentSpaceKey: string | undefined,
): string | undefined => {
	if (!rawSpaceKey) {
		return undefined;
	}
	if (rawSpaceKey === CURRENT_SPACE_KEY_TOKEN) {
		return currentSpaceKey ?? rawSpaceKey;
	}
	return rawSpaceKey;
};
